import { BaseHTMLElement } from '../BaseHTMLElement';
import type { Canvas } from '../../core/Canvas';
import type { ViewportManager } from '../../core/ViewportManager';
import type { ShapeManager } from '../../core/ShapeManager';

export class DebugPanel extends BaseHTMLElement {
    private canvas: Canvas;
    private viewportManager: ViewportManager;
    private shapeManager: ShapeManager;
    private isVisible: boolean = false;
    private updateInterval: number | null = null;
    private mousePos: { screenX: number; screenY: number; worldX: number; worldY: number } = {
        screenX: 0,
        screenY: 0,
        worldX: 0,
        worldY: 0
    };
    
    // Content sections
    private viewportSection!: HTMLElement;
    private shapeSection!: HTMLElement;
    private resizeSection!: HTMLElement;
    private shapeListSection!: HTMLElement;
    private mouseSection!: HTMLElement;
    private performanceSection!: HTMLElement;
    private canvasSection!: HTMLElement;

    constructor(canvas: Canvas) {
        super('div', 'debug-panel');
        this.canvas = canvas;
        this.viewportManager = (canvas as any).viewportManager;
        this.shapeManager = canvas.shapeManager;
        
        this.setupPanel();
        this.setupKeyboardShortcut();
        this.setupMouseTracking();
        this.hide();
    }

    private setupPanel(): void {
        this.element.innerHTML = `
            <div class="debug-header">
                <h3>🐛 Debug Panel</h3>
                <button class="debug-close">×</button>
            </div>
            <div class="debug-content">
                <div class="debug-section" id="viewport-section">
                    <h4>Viewport</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="shape-section">
                    <h4>Shapes Summary</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="resize-section">
                    <h4>Resize Debug</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="shape-list-section">
                    <h4>All Shapes</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="mouse-section">
                    <h4>Mouse Position</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="canvas-section">
                    <h4>Canvas</h4>
                    <div class="debug-values"></div>
                </div>
                <div class="debug-section" id="performance-section">
                    <h4>Performance</h4>
                    <div class="debug-values"></div>
                </div>
            </div>
            <div class="debug-footer">
                Press <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>D</kbd> to toggle
            </div>
        `;

        // Get references to sections
        this.viewportSection = this.element.querySelector('#viewport-section .debug-values')!;
        this.shapeSection = this.element.querySelector('#shape-section .debug-values')!;
        this.resizeSection = this.element.querySelector('#resize-section .debug-values')!;
        this.shapeListSection = this.element.querySelector('#shape-list-section .debug-values')!;
        this.mouseSection = this.element.querySelector('#mouse-section .debug-values')!;
        this.canvasSection = this.element.querySelector('#canvas-section .debug-values')!;
        this.performanceSection = this.element.querySelector('#performance-section .debug-values')!;

        // Close button
        const closeBtn = this.element.querySelector('.debug-close');
        closeBtn?.addEventListener('click', () => this.toggle());
    }

    private setupKeyboardShortcut(): void {
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            // Ctrl + Shift + D to toggle
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                this.toggle();
            }
        });
    }

    private setupMouseTracking(): void {
        window.addEventListener('mousemove', (e: MouseEvent) => {
            this.mousePos.screenX = e.clientX;
            this.mousePos.screenY = e.clientY;
            
            // Convert to world coordinates
            const worldPos = this.viewportManager.screenToWorld(e.clientX, e.clientY);
            this.mousePos.worldX = worldPos.x;
            this.mousePos.worldY = worldPos.y;
        });
    }

    public toggle(): void {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    public show(): void {
        this.isVisible = true;
        this.element.style.display = 'block';
        this.startUpdating();
    }

    public hide(): void {
        this.isVisible = false;
        this.element.style.display = 'none';
        this.stopUpdating();
    }

    private startUpdating(): void {
        if (this.updateInterval) return;
        
        // Update every 100ms
        this.updateInterval = window.setInterval(() => {
            this.updateContent();
        }, 100);
        
        // Initial update
        this.updateContent();
    }

    private stopUpdating(): void {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }

    private updateContent(): void {
        this.updateViewportSection();
        this.updateShapeSection();
        this.updateResizeSection();
        this.updateShapeListSection();
        this.updateMouseSection();
        this.updateCanvasSection();
        this.updatePerformanceSection();
    }

    private updateViewportSection(): void {
        const container = (this.viewportManager as any).container;
        const scale = container.scale.x;
        const position = container.position;

        this.viewportSection.innerHTML = `
            <div class="debug-row">
                <span class="debug-label">Zoom:</span>
                <span class="debug-value">${(scale * 100).toFixed(1)}%</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Pan X:</span>
                <span class="debug-value">${position.x.toFixed(2)}px</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Pan Y:</span>
                <span class="debug-value">${position.y.toFixed(2)}px</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Scale:</span>
                <span class="debug-value">${scale.toFixed(4)}</span>
            </div>
        `;
    }

    private updateShapeSection(): void {
        const shapes = (this.shapeManager as any).shapes;
        const selectedShape = (this.shapeManager as any).selectedShape;
        const isDragging = (this.shapeManager as any).isDragging;
        const isResizing = (this.shapeManager as any).isResizing;

        this.shapeSection.innerHTML = `
            <div class="debug-row">
                <span class="debug-label">Total Shapes:</span>
                <span class="debug-value">${shapes.size}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Selected:</span>
                <span class="debug-value">${selectedShape ? 'Yes' : 'No'}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Dragging:</span>
                <span class="debug-value ${isDragging ? 'debug-active' : ''}">${isDragging ? 'Yes' : 'No'}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Resizing:</span>
                <span class="debug-value ${isResizing ? 'debug-active' : ''}">${isResizing ? 'Yes' : 'No'}</span>
            </div>
        `;
    }

    private updateResizeSection(): void {
        const selectedShape = (this.shapeManager as any).selectedShape;
        const isResizing = (this.shapeManager as any).isResizing;
        const container = (this.viewportManager as any).container;
        const viewportScale = container.scale.x;

        if (!selectedShape) {
            this.resizeSection.innerHTML = `
                <div class="debug-row">
                    <span class="debug-value" style="opacity: 0.5;">No shape selected</span>
                </div>
            `;
            return;
        }

        try {
            const currentScale = selectedShape.getScale ? selectedShape.getScale() : (selectedShape.currentScale || 1);
            const bounds = selectedShape.getBounds ? selectedShape.getBounds() : { width: 0, height: 0 };
            
            // Calculate minimum scale based on current implementation
            let minScale = 0;
            let minDimension = 0;
            let calculatedMinDim = 0;
            
            if (selectedShape.sprite && selectedShape.sprite.texture) {
                // ImageShape logic
                minDimension = 120;
                calculatedMinDim = minDimension / viewportScale;
                const minScaleW = calculatedMinDim / selectedShape.sprite.texture.width;
                const minScaleH = calculatedMinDim / selectedShape.sprite.texture.height;
                minScale = Math.max(minScaleW, minScaleH, 0.05);
            } else {
                // TestShape logic
                minScale = 0.3 / viewportScale;
                minDimension = 30; // TestShape base size * 0.3
                calculatedMinDim = minDimension / viewportScale;
            }

            const distanceFromMin = currentScale - minScale;
            const percentAboveMin = minScale > 0 ? ((currentScale - minScale) / minScale * 100) : 0;
            const isInResistanceZone = currentScale < minScale * 1.5;
            
            // Calculate resize rate (zoom factor that affects resize speed)
            const zoomFactor = 1 / viewportScale;
            const resizeRateMultiplier = zoomFactor;
            
            let resistanceInfo = 'No';
            let effectiveResizeRate = resizeRateMultiplier;
            if (isResizing && isInResistanceZone && distanceFromMin > 0) {
                const range = minScale * 0.5;
                const resistanceFactor = Math.pow(distanceFromMin / range, 1.5);
                resistanceInfo = `Yes (${(resistanceFactor * 100).toFixed(0)}%)`;
                effectiveResizeRate = resizeRateMultiplier * resistanceFactor;
            }

            this.resizeSection.innerHTML = `
                <div class="debug-row">
                    <span class="debug-label">Current Scale:</span>
                    <span class="debug-value ${isResizing ? 'debug-active' : ''}">${currentScale.toFixed(4)}</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Minimum Scale:</span>
                    <span class="debug-value">${minScale.toFixed(4)}</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Above Min:</span>
                    <span class="debug-value ${percentAboveMin < 20 ? 'debug-warning' : ''}">${percentAboveMin.toFixed(1)}%</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Resize Rate:</span>
                    <span class="debug-value ${isResizing ? 'debug-active' : ''}">${resizeRateMultiplier.toFixed(3)}x</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Effective Rate:</span>
                    <span class="debug-value ${isResizing && isInResistanceZone ? 'debug-warning' : ''}">${effectiveResizeRate.toFixed(3)}x</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Dimensions:</span>
                    <span class="debug-value">${bounds.width.toFixed(0)} × ${bounds.height.toFixed(0)}px</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Min Dimension:</span>
                    <span class="debug-value">${calculatedMinDim.toFixed(1)}px (base: ${minDimension})</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Viewport Zoom:</span>
                    <span class="debug-value">${(viewportScale * 100).toFixed(1)}%</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">In Resistance:</span>
                    <span class="debug-value ${isInResistanceZone ? 'debug-warning' : ''}">${isInResistanceZone ? 'Yes' : 'No'}</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Resistance Factor:</span>
                    <span class="debug-value">${resistanceInfo}</span>
                </div>
                <div class="debug-row">
                    <span class="debug-label">Resize Handle:</span>
                    <span class="debug-value">${selectedShape.currentResizeHandle || 'None'}</span>
                </div>
            `;
        } catch (error) {
            console.error('Error in updateResizeSection:', error);
            this.resizeSection.innerHTML = `
                <div class="debug-row">
                    <span class="debug-value" style="color: #ff6600;">Error loading resize info</span>
                </div>
            `;
        }
    }

    private updateShapeListSection(): void {
        const shapes = (this.shapeManager as any).shapes;
        const selectedShape = (this.shapeManager as any).selectedShape;

        if (shapes.size === 0) {
            this.shapeListSection.innerHTML = `
                <div class="debug-row">
                    <span class="debug-value" style="opacity: 0.5;">No shapes on canvas</span>
                </div>
            `;
            return;
        }

        let html = '';
        let shapeIndex = 0;
        
        try {
            shapes.forEach((shape: any) => {
                try {
                    const pos = shape.container?.position || { x: 0, y: 0 };
                    const scale = shape.getScale ? shape.getScale() : (shape.currentScale || 1);
                    const bounds = shape.getBounds ? shape.getBounds() : { width: 0, height: 0 };
                    const isSelected = shape === selectedShape;
                    const shapeType = shape.constructor?.name || 'Unknown';
                    
                    html += `
                        <div class="debug-shape-item ${isSelected ? 'debug-shape-selected' : ''}">
                            <div class="debug-shape-header">
                                <span class="debug-label">${shapeType} #${shapeIndex + 1}</span>
                                ${isSelected ? '<span class="debug-badge">SELECTED</span>' : ''}
                            </div>
                            <div class="debug-shape-details">
                                <div class="debug-row-compact">
                                    <span class="debug-label-sm">Pos:</span>
                                    <span class="debug-value-sm">(${pos.x.toFixed(1)}, ${pos.y.toFixed(1)})</span>
                                </div>
                                <div class="debug-row-compact">
                                    <span class="debug-label-sm">Scale:</span>
                                    <span class="debug-value-sm">${scale.toFixed(3)}</span>
                                </div>
                                <div class="debug-row-compact">
                                    <span class="debug-label-sm">Size:</span>
                                    <span class="debug-value-sm">${bounds.width.toFixed(0)} × ${bounds.height.toFixed(0)}</span>
                                </div>
                                <div class="debug-row-compact">
                                    <span class="debug-label-sm">Visible:</span>
                                    <span class="debug-value-sm">${shape.container?.visible ? 'Yes' : 'No'}</span>
                                </div>
                                <div class="debug-row-compact">
                                    <span class="debug-label-sm">Alpha:</span>
                                    <span class="debug-value-sm">${shape.container?.alpha?.toFixed(2) || '1.00'}</span>
                                </div>
                            </div>
                        </div>
                    `;
                    shapeIndex++;
                } catch (shapeError) {
                    console.error('Error rendering shape in debug panel:', shapeError);
                    html += `
                        <div class="debug-shape-item">
                            <div class="debug-shape-header">
                                <span class="debug-label" style="color: #ff6600;">Error Shape #${shapeIndex + 1}</span>
                            </div>
                        </div>
                    `;
                    shapeIndex++;
                }
            });
        } catch (error) {
            console.error('Error in updateShapeListSection:', error);
            html = `
                <div class="debug-row">
                    <span class="debug-value" style="color: #ff6600;">Error loading shapes</span>
                </div>
            `;
        }

        this.shapeListSection.innerHTML = html;
    }

    private updateMouseSection(): void {
        this.mouseSection.innerHTML = `
            <div class="debug-row">
                <span class="debug-label">Screen:</span>
                <span class="debug-value">(${this.mousePos.screenX}, ${this.mousePos.screenY})</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">World:</span>
                <span class="debug-value">(${this.mousePos.worldX.toFixed(2)}, ${this.mousePos.worldY.toFixed(2)})</span>
            </div>
        `;
    }

    private updateCanvasSection(): void {
        const app = this.canvas.app;
        const stage = app.stage;
        const renderer = app.renderer;

        this.canvasSection.innerHTML = `
            <div class="debug-row">
                <span class="debug-label">Size:</span>
                <span class="debug-value">${app.screen.width} × ${app.screen.height}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Stage Children:</span>
                <span class="debug-value">${stage.children.length}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Renderer:</span>
                <span class="debug-value">${renderer.type === 1 ? 'WebGL' : 'Canvas'}</span>
            </div>
        `;
    }

    private updatePerformanceSection(): void {
        const app = this.canvas.app;
        const fps = app.ticker.FPS.toFixed(1);
        const deltaTime = app.ticker.deltaTime.toFixed(2);

        this.performanceSection.innerHTML = `
            <div class="debug-row">
                <span class="debug-label">FPS:</span>
                <span class="debug-value ${parseFloat(fps) < 50 ? 'debug-warning' : ''}">${fps}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Delta Time:</span>
                <span class="debug-value">${deltaTime}</span>
            </div>
            <div class="debug-row">
                <span class="debug-label">Ticker:</span>
                <span class="debug-value">${app.ticker.started ? 'Running' : 'Stopped'}</span>
            </div>
        `;
    }

    public cleanup(): void {
        this.stopUpdating();
        if (this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
    }
}
